import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Truck, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Dashboard card: open purchase orders past their expected date, each with a one-click
// "ask the supplier for an ETA" email (the email carries the confirm-ship-date link).
export const OverduePOs = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(
    () =>
      api
        .get("/purchase-orders/overdue")
        .then(({ data }) => setRows(data))
        .catch(() => setRows([])),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);

  const nudge = async (po) => {
    if (!po.supplier_email && !po.supplier_notified_to) {
      toast.error(`No email on ${po.supplier_name || "this supplier"} — open the PO to type one`);
      return;
    }
    setBusy(po.id);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/nudge`, {
        to: "",
        cc: "",
        message: "",
      });
      toast.success(`Asked ${data.email.to} for an ETA on ${po.code}`);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not send the reminder",
      );
    }
    setBusy("");
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-4" data-testid="overdue-pos">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck size={16} weight="bold" className="text-amber-300" />
          <div className="text-xs uppercase tracking-widest text-zinc-500">Overdue Purchase Orders</div>
        </div>
        <Link
          to="/inventory/purchase-orders"
          className="text-xs text-yellow-400 hover:underline"
          data-testid="overdue-pos-link"
        >
          All orders
        </Link>
      </div>
      <div className="mt-4 space-y-2" data-testid="overdue-pos-list">
        {rows && rows.length === 0 && (
          <p className="text-sm text-zinc-500" data-testid="overdue-pos-empty">
            Nothing overdue — suppliers are on time.
          </p>
        )}
        {(rows || []).slice(0, 6).map((po) => (
          <div
            key={po.id}
            data-testid={`overdue-po-${po.id}`}
            className="flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3"
          >
            <Link to={`/inventory/purchase-orders/${po.id}`} className="min-w-0 flex-1">
              <div className="font-mono-data text-[10px] text-yellow-500">{po.code}</div>
              <div className="truncate text-sm font-semibold">{po.supplier_name || "No supplier"}</div>
              <div className="text-xs text-zinc-500">
                Expected {po.expected_date}
                {po.supplier_confirmation?.ship_date
                  ? ` · supplier said ${po.supplier_confirmation.ship_date}`
                  : ""}
                {po.eta_requested_at ? ` · asked ${po.eta_requested_at.slice(0, 10)}` : ""}
              </div>
            </Link>
            <span className="font-mono-data shrink-0 text-xs font-bold text-amber-300">
              {po.days_late}d late
            </span>
            <button
              type="button"
              data-testid={`po-nudge-${po.id}`}
              disabled={busy === po.id}
              onClick={() => nudge(po)}
              title="Email the supplier asking for a ship date"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-amber-500/40 px-2.5 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors duration-150 hover:bg-amber-500/15 disabled:opacity-50"
            >
              <PaperPlaneTilt size={12} weight="bold" />
              {busy === po.id ? "Sending…" : po.eta_requested_at ? "Ask again" : "Ask for ETA"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
